import { makeAutoObservable, runInAction } from 'mobx';
import { Common } from 'src/domains/common/Common';
import { Amount } from 'src_common/common/amount/Amount';
import { AccountState } from 'src/domains/players/state/accountState/AccountState';
import { AccountModel } from 'src/domains/players/state/accountState/AccountModel';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { GoogleTagManagerState } from 'src/domains/layouts/state/googleState/GoogleTagManagerState';
import { UsersState } from 'src/domains/players/state/UsersState';
import { BasicDataModel } from 'src/domains/players/state/BasicDataModel';
import {
    BillingInfoSuccess,
    billingInfoFieldsValidationDeposit,
} from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/helpers/billingInfoFiledsValidation/realexBillingInfoFieldsValidation';
import { MultiDepositSteps } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/multiDepositJourney/MultiDepositSteps.state';
import { MessageDataType } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/Iframe';
import { DepositErrorMessagesState } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/errorMessages/DepositErrorMessages.state';

export class RealexTabState {
    public isLoading: boolean = true;
    public transactionId: number | undefined = undefined;
    public formActionUrl: string = '';
    public formData: Record<string, string> = {};

    private readonly account: AccountState;
    private readonly googleTagManager: GoogleTagManagerState;
    private readonly usersState: UsersState;
    private readonly basicDataModel: BasicDataModel;
    private readonly configComponents: ConfigComponents;
    private addedDepositTag: boolean = false;

    public constructor(
        public readonly common: Common,
        private readonly amount: string,
        private readonly stepsState: MultiDepositSteps,
        private readonly isSignup: boolean,
        private readonly errorMessagesState: DepositErrorMessagesState
    ) {
        this.account = AccountState.get(common);
        this.googleTagManager = GoogleTagManagerState.get(common);
        this.usersState = UsersState.get(common);
        this.basicDataModel = BasicDataModel.get(common);
        this.configComponents = ConfigComponents.get(common);

        makeAutoObservable(this);

        this.initializePayment();
    }

    private initializePayment = (): void => {
        setTimeout(() => {
            void this.submitDepositForm();
        }, 0);
    };

    private submitDepositForm = async (): Promise<void> => {
        const amountObj = new Amount(this.amount);
        const oldFormatAmount = this.configComponents.precision.valueOldFormat(amountObj);
        const account = this.account.account;

        if (account === null) {
            console.error('submitDepositForm - User is anonymous');
            this.stepsState.redirectToSetAmount();
            return;
        }

        const billingInfo = billingInfoFieldsValidationDeposit(this.basicDataModel.basicDataReady);
        if (billingInfo?.type !== 'ok') {
            console.error('submitDepositForm - No basic user data');
            this.stepsState.redirectToSetAmount();
            return;
        }

        try {
            const hasRingFencedFunds = this.basicDataModel.basicDataReady?.ringFencedFunds ?? false;
            if (!hasRingFencedFunds) {
                await this.usersState.onChangeRingFencedFlag();
            }

            if (this.isSignup) {
                this.googleTagManager.depositedMoney(oldFormatAmount, true);
                this.googleTagManager.gtmSignUpStepFour(oldFormatAmount);
            } else {
                this.googleTagManager.depositedMoney(oldFormatAmount, false);
            }

            await this.onSubmitCard(oldFormatAmount, billingInfo.data, account);
        } catch (e) {
            this.stepsState.redirectToSetAmount();
            this.errorMessagesState.createErrorMessage('unknown');
            throw e;
        }
    };

    private onSubmitCard = async (
        amount: number,
        billingInfoData: BillingInfoSuccess['data'],
        account: AccountModel
    ): Promise<void> => {
        const response = await account.onSendTopUpAmount({
            requestBody: {
                amount,
                paymentMethod: undefined,
                billingInfo: billingInfoData,
                channel: 'default',
                status: 'initiated',
            },
        });

        if (response.status === '200') {
            const { bodyJson } = response;
            runInAction(() => {
                this.transactionId = bodyJson.id;
            });

            const paymentForm = bodyJson.paymentForm ?? false;

            if (bodyJson.status === 'failed') {
                if (bodyJson.reason === 'invalid-data') {
                    this.errorMessagesState.createErrorMessage('invalid-data');
                    this.stepsState.redirectToSetAmount();
                } else {
                    this.stepsState.redirectToFinalView(this.transactionId);
                }
            } else if (paymentForm === false) {
                throw new Error('Payment form is null');
            } else {
                runInAction(() => {
                    this.formActionUrl = paymentForm.formActionUrl;
                    this.formData = paymentForm.formData;
                    this.isLoading = false;
                });
            }
        } else {
            const { errors } = response;
            const error = errors[0];

            if (error === undefined) {
                this.stepsState.redirectToFailureView('serverIssue');
                return;
            }

            if (typeof error.code === 'string') {
                this.errorMessagesState.createErrorMessage(error.code, error.debugDetails ?? undefined);
                this.stepsState.redirectToSetAmount();
            } else {
                this.stepsState.redirectToFinalView(undefined);
            }
        }
    };

    public onMessage = (data: MessageDataType): void => {
        if (data.type === 'success') {
            this.stepsState.redirectToFinalView(this.transactionId);

            if (this.addedDepositTag === false) {
                runInAction(() => {
                    this.addedDepositTag = true;
                });
                this.googleTagManager.addDepositTag(this.account.account);
            }
        } else {
            this.stepsState.redirectToFinalView(this.transactionId);
        }
    };
}
